import { ApolloError, QueryResult, useQuery, useMutation, gql } from '@apollo/client';
import { FavoriteResource, Resource } from '../../../providers/ResourcesProvider/types';
import { StoreTypename } from '../../../types';
import ADD_FAVORITE_RESOURCE from '../../../graph/queries/resources/add-favorite-resource-query';
import REMOVE_FAVORITE_RESOURCE from '../../../graph/queries/resources/remove-favorite-resource-query';
import GET_RESOURCES from '../../../graph/queries/resources/get-resources-query';
import GET_FAVORITE_RESOURCES from '../../../graph/queries/resources/get-favorite-resources-query';

export type ResourcesStoreState = {
  resources: Resource[];
  error: ApolloError | undefined;
  isLoading: boolean;
};
export type ResourcesStoreDispatch = {
  pinResource: (resourceId: number) => Promise<FavoriteResource | void>;
  unpinResource: (favoriteResourceId: number) => Promise<void>;
};
export type ResourcesStore = [ResourcesStoreState, ResourcesStoreDispatch];

export const getDefaultResourcesStoreState = (): { __typename: string } => ({
  __typename: StoreTypename.RESOURCES,
});

const getQueryVariables = (character?: string) => {
  if (!character) return undefined;
  return {
    variables: { where: { character: { character } } },
  };
};

const SUPPORTRESOURCETYPEID = 8;

const useResourcesStore = (letter?: string): ResourcesStore => {
  const { data, loading, error }: QueryResult<{ resources: Resource[] }> = useQuery(
    GET_RESOURCES,
    getQueryVariables(letter)
  );

  const [mutatePinResource] = useMutation(ADD_FAVORITE_RESOURCE, {
    update(cache, { data: { createFavorite } }) {
      cache.modify({
        fields: {
          favorites(existingFavorites = []) {
            const newFavoriteResourceRef = cache.writeFragment({
              data: createFavorite,
              fragment: gql`
                fragment NewFavorite on Favorite {
                  id
                  resourceId
                  userId
                  resource
                }
              `,
            });
            return [...existingFavorites, newFavoriteResourceRef];
          },
        },
      });
    },
  });
  const [mutateUnpinResource] = useMutation(REMOVE_FAVORITE_RESOURCE, {
    update(cache, { data: { deleteFavorite } }) {
      const existingFavoriteResources: { favorites: FavoriteResource[] } | null = cache.readQuery({
        query: GET_FAVORITE_RESOURCES,
      });
      const filteredFavoriteResources = existingFavoriteResources
        ? existingFavoriteResources.favorites.filter(
            (favorite: FavoriteResource) => favorite.id !== deleteFavorite.id
          )
        : [];
      cache.evict({
        fieldName: 'favorites',
        broadcast: false,
      });
      cache.writeQuery({
        query: GET_FAVORITE_RESOURCES,
        data: { favorites: filteredFavoriteResources },
      });
    },
  });

  return [
    {
      // TODO: Find better way to not return support based resources
      resources: (data?.resources || []).filter(
        (resource) => resource.type.id !== SUPPORTRESOURCETYPEID
      ),
      error,
      isLoading: loading,
    },
    {
      pinResource: async (resourceId: number): Promise<FavoriteResource | void> => {
        try {
          const { data: record } = <{ data: { createFavorite: FavoriteResource } }>(
            await mutatePinResource({ variables: { data: { resourceId } } })
          );

          return record.createFavorite;
        } catch (error) {
          console.log(error);
        }
      },
      unpinResource: async (favoriteResourceId: number) => {
        try {
          await mutateUnpinResource({ variables: { data: { id: favoriteResourceId } } });
        } catch (error) {
          console.log(error);
        }
      },
    },
  ];
};

export default useResourcesStore;
